import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AlertCircle, X, Eye, EyeOff, Trash2, Lock, Edit2, Save, Mail, ExternalLink, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { PAYMENT_DETAILS } from "../../routes/routes";
import ApiCollections from "../../configs/services/apiCollections";
import { signOutRequest } from "../../redux/auth/auth.actions";

const ProfileSettingsPage = styled.div`
  background: #ededed;
  min-height: 100%;
  width: 100%;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`;

const ProfileSettingsContainer = styled.div`
  padding: 2rem;
  width: 100%;
`;

const SettingsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const SettingsSection = styled.div`
  margin-bottom: 0;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const SectionDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
`;

const InfoButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(59, 130, 246, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.$isOpen ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fef3c7;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ImportanceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ImportanceIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #fef3c7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
    color: #d97706;
  }
`;

const ImportanceContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ImportanceTitle = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const ImportanceText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
`;

const ReasonList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReasonItem = styled.li`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const ReasonIcon = styled.div`
  width: 24px;
  height: 24px;
  background: #dbeafe;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0.125rem;

  &::before {
    content: "✓";
    color: #2563eb;
    font-weight: bold;
    font-size: 0.875rem;
  }
`;

const ReasonContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ReasonTitle = styled.strong`
  font-size: 0.875rem;
  color: #111827;
  font-weight: 600;
`;

const ReasonDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 0;
`;

const ModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  background: #f9fafb;
`;

const CloseModalButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
  }
`;

const DangerSection = styled.div`
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 2px solid #fee2e2;
`;

const DangerTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #dc2626;
  margin-bottom: 0.5rem;
`;

const DangerDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
`;

const ChangePasswordButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(59, 130, 246, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const DeleteAccountButton = styled.button`
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #b91c1c;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(220, 38, 38, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 0.75rem 2.75rem 0.75rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #111827;
  background: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: #111827;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0;
`;

const ModalFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0;
`;

const FormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const ErrorMessage = styled.p`
  font-size: 0.75rem;
  color: #dc2626;
  margin: 0;
`;

const DeleteModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  background: #f9fafb;
`;

const CancelButton = styled.button`
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }
`;

const ConfirmDeleteButton = styled.button`
  background: #dc2626;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #b91c1c;
  }

  &:disabled {
    background: #fca5a5;
    cursor: not-allowed;
  }
`;

const DeleteModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fee2e2;
`;

const DeleteModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #dc2626;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const DeleteModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const WarningText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
`;

const ChangePasswordModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eff6ff;
`;

const ChangePasswordModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e40af;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ChangePasswordModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ChangePasswordModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  background: #f9fafb;
`;

const SavePasswordButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`;

const EditableSection = styled.div`
  background: transparent;
`;

const ExpandToggleButton = styled.button`
  display: none;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  min-width: 1.75rem;
  min-height: 1.75rem;
  padding: 0;
  margin: 0;
  flex: 0 0 auto;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    transform 0.15s ease;
  transform: rotate(${(props) => (props.$expanded ? "180deg" : "0deg")});

  svg {
    display: block;
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    stroke: currentColor;
  }

  &:hover {
    background: #e5e7eb;
    border-color: #d1d5db;
  }
`;

const CollapsibleTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const SectionHeaderTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EditButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SaveButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  margin-bottom: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const FormRowTwoColumn = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 2rem;
  margin-bottom: 0.5rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const FormLabelLeft = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  padding-top: 0.75rem;
`;

const FormInputRight = styled.div`
  flex: 1;
`;

const FieldValue = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  padding-top: 0.75rem;
`;

const SectionDivider = styled.div`
  border-top: 1px dashed #e5e7eb;
  margin: 2rem 0;
`;

const SectionTitleLabel = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1.5rem 0;
  padding: 0;
`;

const AddressHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const EditAddressLink = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }

  &:active {
    color: #1d4ed8;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const FieldLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.25rem;
`;

const TextInput = styled.input`
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #111827;
  background: white;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
    border-color: #e5e7eb;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const SelectInput = styled.select`
  padding: 0.875rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #111827;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background-color: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
    border-color: #e5e7eb;
  }
`;

const FullWidthField = styled(FormField)`
  grid-column: 1 / -1;
`;

const UserInfoDisplay = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 1.25rem;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const UserInfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const UserInfoLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const UserInfoValue = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
`;

const ChangeEmailLink = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }

  &:active {
    color: #1d4ed8;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const EmailValueContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const ChangeEmailModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChangeEmailModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e40af;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChangeEmailModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
`;

const ChangeEmailModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  background: #f9fafb;
`;

const ChangeAddressModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChangeAddressModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e40af;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChangeAddressModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const ChangeAddressModalFooter = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  background: #f9fafb;
`;

const SaveEmailButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`;

const VerificationMessage = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 1.25rem;
  margin-top: 1rem;
`;

const VerificationText = styled.p`
  font-size: 0.875rem;
  color: #1e40af;
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
`;

const VerificationLink = styled.a`
  color: #3b82f6;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const VerificationLinkButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`;

const SectionActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 2px solid #f3f4f6;
`;

const SectionCancelButton = styled.button`
  background: #f3f4f6;
  color: #374151;
  border: 2px solid #e5e7eb;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
    border-color: #d1d5db;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SelectionButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const SelectionButton = styled.button`
  padding: 0.875rem 1.5rem;
  border: 2px solid ${(props) => (props.$selected ? "#3b82f6" : "#e5e7eb")};
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.$selected ? "#3b82f6" : "white")};
  color: ${(props) => (props.$selected ? "white" : "#374151")};
  box-shadow: ${(props) => (props.$selected ? "0 2px 4px rgba(59, 130, 246, 0.3)" : "none")};

  &:hover {
    border-color: #3b82f6;
    background: ${(props) => (props.$selected ? "#2563eb" : "#eff6ff")};
    transform: ${(props) => (props.$selected ? "translateY(-1px)" : "none")};
    box-shadow: ${(props) => (props.$selected ? "0 4px 8px rgba(59, 130, 246, 0.4)" : "0 2px 4px rgba(0, 0, 0, 0.1)")};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const PlanCard = styled.div`
  background: ${(props) => (props.$selected ? "#f5f8fe" : "#fafafa")};
  border: 1px solid ${(props) => (props.$selected ? "#d1d5db" : "rgba(17, 24, 39, 0.08)")};
  border-radius: 12px;
  padding: 2rem 1.5rem;
  position: relative;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  &:hover {
    border-color: #d1d5db;
  }
`;

const PlanBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: #d97706;
  color: white;
  padding: 0.375rem 1rem;
  border-radius: 0 12px 0 12px;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PlanName = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;
`;

const PlanFeatures = styled.div`
  font-size: 0.875rem;
  color: #4b5563;
  margin-top: 0.5rem;
  line-height: 1.5;
`;

const PlanButton = styled.button`
  width: 100%;
  padding: 0.625rem 1rem;
  border: 1px solid ${(props) => (props.$selected ? "#3b82f6" : "#e5e7eb")};
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  background: ${(props) => (props.$selected ? "#3b82f6" : "white")};
  color: ${(props) => (props.$selected ? "white" : "#374151")};
  margin-top: auto;
  box-shadow: ${(props) => (props.$selected ? "0 1px 2px rgba(59, 130, 246, 0.2)" : "0 1px 2px 0 rgba(0, 0, 0, 0.05)")};

  &:hover {
    background: ${(props) => (props.$selected ? "#2563eb" : "#f9fafb")};
    border-color: ${(props) => (props.$selected ? "#2563eb" : "#9ca3af")};
  }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0;
  margin-bottom: 1rem;
  transition: color 0.2s ease;

  &:hover {
    color: #2563eb;
    text-decoration: underline;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SelectedPlanInfo = styled.div`
  background: #eff6ff;
  border: 2px solid #3b82f6;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
`;

const PlanInfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PlanInfoName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
`;

const PlanInfoDetails = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const PromoCodeSection = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const PromoCodeInput = styled(TextInput)`
  flex: 1;
`;

const ApplyButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`;

const OrderSummary = styled.div`
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const OrderSummaryTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 1rem 0;
`;

const OrderSummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.875rem;

  &:last-child {
    border-bottom: none;
    font-weight: 600;
    font-size: 1rem;
    padding-top: 1rem;
    margin-top: 0.5rem;
    border-top: 2px solid #e5e7eb;
  }
`;

const OrderSummaryLabel = styled.span`
  color: #6b7280;
`;

const OrderSummaryValue = styled.span`
  color: #111827;
  font-weight: 500;
`;

const PaymentMethodSection = styled.div`
  margin-bottom: 1.5rem;
`;

const LinkedCardInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const LinkedCardDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`;

const CardBrand = styled.div`
  font-weight: 600;
  color: #111827;
  font-size: 0.875rem;
`;

const CardLast4 = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;

const LinkCardButton = styled.button`
  background: #22c55e;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #16a34a;
  }
`;

const CardInputWrapper = styled.div`
  position: relative;
`;

const CardIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  display: flex;
  align-items: center;
`;

const CardNumberInput = styled(TextInput)`
  padding-left: 2.75rem;
`;

const TrialInfo = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  color: #1e40af;
  line-height: 1.5;
`;

const StartTrialButton = styled.button`
  width: 100%;
  background: #3b82f6;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    background: #93c5fd;
    cursor: not-allowed;
  }
`;

function ProfileSettings() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Change password states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePasswordErrors, setChangePasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Change email states
  const [isChangeEmailModalOpen, setIsChangeEmailModalOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [changeEmailError, setChangeEmailError] = useState("");
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [verificationData, setVerificationData] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const dispatch = useDispatch();

  // Helper function to extract error message from API response
  const getErrorMessage = (error, defaultMessage = "An error occurred") => {
    // Check for errors object format: { errors: { field: ['message'] } }
    if (error?.response?.data?.errors) {
      const errors = error.response.data.errors;
      // Get the first error from the first field
      const firstField = Object.keys(errors)[0];
      if (firstField && errors[firstField] && errors[firstField].length > 0) {
        return errors[firstField][0];
      }
    }
    // Fall back to message field
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    // Fall back to field-specific errors (e.g., email[0])
    if (error?.response?.data?.email?.[0]) {
      return error.response.data.email[0];
    }
    // Fall back to error message
    if (error?.message) {
      return error.message;
    }
    return defaultMessage;
  };

  // Address section states
  const [isAddressEditing, setIsAddressEditing] = useState(false);
  const [isAddressSaving, setIsAddressSaving] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [tempAddressData, setTempAddressData] = useState({
    country: "United States",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
  });
  const [addressData, setAddressData] = useState({
    country: "United States",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  // Organization section states
  const [isOrganizationModalOpen, setIsOrganizationModalOpen] = useState(false);
  const [isOrganizationSaving, setIsOrganizationSaving] = useState(false);
  const [tempOrganizationData, setTempOrganizationData] = useState({
    businessName: "",
    businessType: "",
    teamMembers: "",
    totalCards: "",
  });
  const [organizationData, setOrganizationData] = useState({
    businessName: "",
    businessType: "",
    teamMembers: "",
    totalCards: "",
  });

  // Plan section states
  const [selectedPlan, setSelectedPlan] = useState("");
  const navigate = useNavigate();

  // Collapsible section states
  const [isPersonalInfoExpanded, setIsPersonalInfoExpanded] = useState(true);
  const [isAddressExpanded, setIsAddressExpanded] = useState(true);
  const [isOrganizationExpanded, setIsOrganizationExpanded] = useState(true);
  const [isSelectPlanExpanded, setIsSelectPlanExpanded] = useState(true);
  const [isSecurityExpanded, setIsSecurityExpanded] = useState(true);

  // User data state
  const [userData, setUserData] = useState({
    username: "",
    email: "",
  });

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await ApiCollections.getMe();
        console.log("User data from /api/v1/auth/me/:", response);
        setUserData({
          username: response.username || "",
          email: response.email || "",
        });
        // Set address data from API response
        if (response.address) {
          setAddressData({
            country: response.address.country || "United States",
            addressLine1: response.address.addressLine1 || "",
            addressLine2: response.address.addressLine2 || "",
            city: response.address.city || "",
            state: response.address.state || "",
            zipCode: response.address.zipCode || "",
          });
        }
        // Set organization data from API response
        if (response.org_details) {
          setOrganizationData({
            businessName: response.org_details.businessName || "",
            businessType: response.org_details.businessType || "",
            teamMembers: response.org_details.teamMembers || "",
            totalCards: response.org_details.totalCards || "",
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  const handleOpenDeleteModal = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseDeleteModal();
    }
  };

  const handleOpenChangePasswordModal = () => {
    setIsChangePasswordModalOpen(true);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setChangePasswordErrors({});
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleCloseChangePasswordModal = () => {
    setIsChangePasswordModalOpen(false);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setChangePasswordErrors({});
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleChangePasswordOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseChangePasswordModal();
    }
  };

  const handleOpenChangeEmailModal = () => {
    setIsChangeEmailModalOpen(true);
    setNewEmail("");
    setChangeEmailError("");
    setVerificationData(null);
  };

  const handleCloseChangeEmailModal = () => {
    setIsChangeEmailModalOpen(false);
    setNewEmail("");
    setChangeEmailError("");
    setVerificationData(null);
  };

  const handleChangeEmailOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseChangeEmailModal();
    }
  };

  const handleChangeEmail = async () => {
    if (!newEmail.trim()) {
      setChangeEmailError("Email is required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setChangeEmailError("Please enter a valid email address");
      return;
    }

    setIsChangingEmail(true);
    setChangeEmailError("");

    try {
      const response = await ApiCollections.updateMe({ email: newEmail });
      
      // Store verification data and show verification UI
      if (response?.verification_url) {
        setVerificationData({
          message: response.message,
          verification_url: response.verification_url,
          new_email: response.new_email,
          expires_in: response.expires_in,
        });
        toast.success(response?.message || "Verification email sent");
      } else {
        // If no verification needed, update user data and close
        if (response) {
          setUserData({
            username: response.username || userData.username,
            email: response.email || newEmail,
          });
        }
        toast.success(response?.message || "Email updated successfully");
        handleCloseChangeEmailModal();
      }
    } catch (error) {
      console.error("Change email error:", error);
      const errorMessage = getErrorMessage(error, "Failed to update email");
      setChangeEmailError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsChangingEmail(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (!verificationData?.verification_url) {
      return;
    }

    setIsVerifying(true);
    setChangeEmailError("");

    try {
      const response = await fetch(verificationData.verification_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Check for errors object format
        if (errorData?.errors) {
          const firstField = Object.keys(errorData.errors)[0];
          const errorMessage = firstField && errorData.errors[firstField]?.[0] 
            ? errorData.errors[firstField][0] 
            : errorData?.message || "Email verification failed";
          throw new Error(errorMessage);
        }
        throw new Error(errorData?.message || "Email verification failed");
      }

      const data = await response.json();
      
      // Logout user after successful verification
      toast.success("Email changed successfully. Please login using your new email.");
      dispatch(signOutRequest({ func: () => {} }));
      
      // Close modal
      handleCloseChangeEmailModal();
    } catch (error) {
      console.error("Verify email error:", error);
      const errorMessage = error?.message || "Failed to verify email. Please try again.";
      setChangeEmailError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsVerifying(false);
    }
  };

  const validateChangePassword = () => {
    const errors = {};

    if (!currentPassword.trim()) {
      errors.currentPassword = "Current password is required";
    }

    if (!newPassword.trim()) {
      errors.newPassword = "New password is required";
    } else if (newPassword.length < 8) {
      errors.newPassword = "Password must be at least 8 characters long";
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = "Please confirm your new password";
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (currentPassword === newPassword && currentPassword.trim()) {
      errors.newPassword = "New password must be different from current password";
    }

    setChangePasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validateChangePassword()) {
      return;
    }

    setIsChangingPassword(true);

    try {
      const response = await ApiCollections.updateMe({
        old_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });

      toast.success(response?.message || "Password changed successfully. Please login again.");
      handleCloseChangePasswordModal();

      // Logout user after successful password change so the old session/token can't be reused
      dispatch(signOutRequest({ func: () => {} }));
    } catch (error) {
      console.error("Change password error:", error);
      const errorMessage = getErrorMessage(error, "Failed to change password. Please check your current password.");
      setChangePasswordErrors({ currentPassword: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleAddressEdit = () => {
    setTempAddressData({ ...addressData });
    setIsAddressModalOpen(true);
  };

  const handleAddressCancel = () => {
    setIsAddressModalOpen(false);
    setTempAddressData({
      country: "United States",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      zipCode: "",
    });
  };

  const handleAddressOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleAddressCancel();
    }
  };

  const handleAddressSave = async () => {
    setIsAddressSaving(true);
    try {
      // Send address in the required payload structure
      await ApiCollections.updateMe({ 
        address: {
          country: tempAddressData.country,
          addressLine1: tempAddressData.addressLine1,
          addressLine2: tempAddressData.addressLine2,
          city: tempAddressData.city,
          state: tempAddressData.state,
          zipCode: tempAddressData.zipCode,
        }
      });
      
      // Refresh profile data to get latest address from API
      const response = await ApiCollections.getMe();
      if (response.address) {
        setAddressData({
          country: response.address.country || "United States",
          addressLine1: response.address.addressLine1 || "",
          addressLine2: response.address.addressLine2 || "",
          city: response.address.city || "",
          state: response.address.state || "",
          zipCode: response.address.zipCode || "",
        });
      }
      
      toast.success("Profile updated successfully");
      setIsAddressModalOpen(false);
    } catch (error) {
      console.error("Save profile error:", error);
      const errorMessage = getErrorMessage(error, "Failed to update profile");
      toast.error(errorMessage);
    } finally {
      setIsAddressSaving(false);
    }
  };

  const handleOrganizationEdit = () => {
    setTempOrganizationData({ ...organizationData });
    setIsOrganizationModalOpen(true);
  };

  const handleOrganizationCancel = () => {
    setIsOrganizationModalOpen(false);
    setTempOrganizationData({
      businessName: "",
      businessType: "",
      teamMembers: "",
      totalCards: "",
    });
  };

  const handleOrganizationOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleOrganizationCancel();
    }
  };

  const handleOrganizationSave = async () => {
    setIsOrganizationSaving(true);
    try {
      // Send organization data in the required payload structure - same pattern as address
      await ApiCollections.updateMe({ 
        org_details: {
          businessName: tempOrganizationData.businessName,
          businessType: tempOrganizationData.businessType,
          teamMembers: tempOrganizationData.teamMembers,
          totalCards: tempOrganizationData.totalCards,
        }
      });
      
      // Refresh profile data to get latest organization from API
      const response = await ApiCollections.getMe();
      if (response.org_details) {
        setOrganizationData({
          businessName: response.org_details.businessName || "",
          businessType: response.org_details.businessType || "",
          teamMembers: response.org_details.teamMembers || "",
          totalCards: response.org_details.totalCards || "",
        });
      }
      
      toast.success("Organization details saved successfully");
      setIsOrganizationModalOpen(false);
    } catch (error) {
      console.error("Save organization error:", error);
      const errorMessage = getErrorMessage(error, "Failed to save organization details");
      toast.error(errorMessage);
    } finally {
      setIsOrganizationSaving(false);
    }
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    // Navigate to payment details page with selected plan
    navigate(PAYMENT_DETAILS, { state: { selectedPlan: planId } });
  };

  const plans = [
    {
      id: "prospect",
      name: "Prospect",
      price: "$9",
      period: "month",
      scans: "500 monthly AI scans",
      buttonText: "1-Week Free Trial",
      badge: null,
    },
    {
      id: "minors",
      name: "Minors",
      price: "$19",
      period: "month",
      scans: "1,100 monthly AI scans",
      buttonText: "Get Started",
      badge: null,
    },
    {
      id: "rookie",
      name: "Rookie",
      price: "$39",
      period: "month",
      scans: "2,500 monthly AI scans",
      buttonText: "Get Started",
      badge: null,
    },
    {
      id: "all-star",
      name: "All-Star",
      price: "$59",
      period: "month",
      scans: "5,000 monthly AI scans",
      buttonText: "Get Started",
      badge: "Most Popular",
    },
    {
      id: "mvp",
      name: "MVP",
      price: "$99",
      period: "month",
      scans: "10,000 monthly AI scans",
      buttonText: "Get Started",
      badge: null,
    },
    {
      id: "hall-of-fame",
      name: "Hall of Fame",
      price: "$199",
      period: "month",
      scans: "25,000 monthly AI scans",
      buttonText: "Get Started",
      badge: null,
    },
  ];


  const handleDeleteAccount = async () => {
    setIsDeleting(true);

    try {
      await ApiCollections.deleteMe();

      toast.success("Account deleted successfully");
      handleCloseDeleteModal();
      
      // Logout user after successful deletion
      dispatch(signOutRequest({ func: () => {} }));
    } catch (error) {
      console.error("Delete account error:", error);
      const errorMessage = getErrorMessage(error, "Failed to delete account. Please try again.");
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <ProfileSettingsPage>
    <ProfileSettingsContainer>
      <SettingsContent>
        <SettingsSection
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            flexWrap: "wrap",
            paddingBottom: "1.5rem",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div>
            <SectionTitle>Account Information</SectionTitle>
            <SectionDescription style={{ marginBottom: 0 }}>
              Manage your account settings and preferences.
            </SectionDescription>
          </div>
          <InfoButton onClick={handleOpenModal} style={{ marginTop: 0 }}>
            <AlertCircle />
            Why Profile Completion is Mandatory
          </InfoButton>
        </SettingsSection>

        {/* Personal Information Section */}
        <EditableSection>
          <CollapsibleTitleRow
            style={{ marginBottom: "1.5rem" }}
            onClick={() => setIsPersonalInfoExpanded((v) => !v)}
          >
            <SectionHeaderTitle style={{ marginBottom: 0 }}>Personal Information</SectionHeaderTitle>
            <ExpandToggleButton
              type="button"
              $expanded={isPersonalInfoExpanded}
              aria-label={isPersonalInfoExpanded ? "Collapse personal information" : "Expand personal information"}
              onClick={(e) => {
                e.stopPropagation();
                setIsPersonalInfoExpanded((v) => !v);
              }}
            >
              <ChevronDown size={16} />
            </ExpandToggleButton>
          </CollapsibleTitleRow>

          {isPersonalInfoExpanded && (
          <>
          {userData.username && (
            <>
              <FormRowTwoColumn>
                <FormLabelLeft htmlFor="username">Username</FormLabelLeft>
                <FormInputRight>
                  <FieldValue>{userData.username}</FieldValue>
                </FormInputRight>
              </FormRowTwoColumn>

              <FormRowTwoColumn>
                <FormLabelLeft htmlFor="email">Email address</FormLabelLeft>
                <FormInputRight>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "0.75rem" }}>
                    <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#111827" }}>{userData.email}</div>
                    <ChangeEmailLink onClick={handleOpenChangeEmailModal}>
                      <Edit2 size={14} />
                      Change email
                    </ChangeEmailLink>
                  </div>
                </FormInputRight>
              </FormRowTwoColumn>
            </>
          )}

          <SectionDivider />

          <AddressHeaderRow>
            <SectionTitleLabel
              style={{ margin: 0, cursor: "pointer" }}
              onClick={() => setIsAddressExpanded((v) => !v)}
            >
              Address
            </SectionTitleLabel>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <EditAddressLink onClick={handleAddressEdit}>
                <Edit2 size={14} />
                Edit address
              </EditAddressLink>
              <ExpandToggleButton
                type="button"
                $expanded={isAddressExpanded}
                aria-label={isAddressExpanded ? "Collapse address" : "Expand address"}
                onClick={() => setIsAddressExpanded((v) => !v)}
              >
                <ChevronDown size={16} />
              </ExpandToggleButton>
            </div>
          </AddressHeaderRow>

          {isAddressExpanded && (
            <>
              <FormRowTwoColumn>
                <FormLabelLeft htmlFor="country">Country</FormLabelLeft>
                <FormInputRight>
                  <FieldValue>{addressData.country || "Not set"}</FieldValue>
                </FormInputRight>
              </FormRowTwoColumn>

              <FormRowTwoColumn>
                <FormLabelLeft htmlFor="addressLine1">Address</FormLabelLeft>
                <FormInputRight>
                  <FieldValue>{addressData.addressLine1 || "Not set"}</FieldValue>
                </FormInputRight>
              </FormRowTwoColumn>

              <FormRowTwoColumn>
                <FormLabelLeft htmlFor="city">City</FormLabelLeft>
                <FormInputRight>
                  <FieldValue>{addressData.city || "Not set"}</FieldValue>
                </FormInputRight>
              </FormRowTwoColumn>

              <FormRowTwoColumn>
                <FormLabelLeft htmlFor="state">State</FormLabelLeft>
                <FormInputRight>
                  <FieldValue>{addressData.state || "Not set"}</FieldValue>
                </FormInputRight>
              </FormRowTwoColumn>

              <FormRowTwoColumn>
                <FormLabelLeft htmlFor="zipCode">Postal code</FormLabelLeft>
                <FormInputRight>
                  <FieldValue>{addressData.zipCode || "Not set"}</FieldValue>
                </FormInputRight>
              </FormRowTwoColumn>
            </>
          )}

          <SectionDivider />

          <AddressHeaderRow>
            <SectionTitleLabel
              style={{ margin: 0, cursor: "pointer" }}
              onClick={() => setIsOrganizationExpanded((v) => !v)}
            >
              Organization Details
            </SectionTitleLabel>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
              <EditAddressLink onClick={handleOrganizationEdit}>
                <Edit2 size={14} />
                Edit
              </EditAddressLink>
              <ExpandToggleButton
                type="button"
                $expanded={isOrganizationExpanded}
                aria-label={isOrganizationExpanded ? "Collapse organization details" : "Expand organization details"}
                onClick={() => setIsOrganizationExpanded((v) => !v)}
              >
                <ChevronDown size={16} />
              </ExpandToggleButton>
            </div>
          </AddressHeaderRow>

          {isOrganizationExpanded && (
            <>
              <FormRowTwoColumn>
                <FormLabelLeft htmlFor="businessName">Business Name</FormLabelLeft>
                <FormInputRight>
                  <FieldValue>{organizationData.businessName || "Not set"}</FieldValue>
                </FormInputRight>
              </FormRowTwoColumn>

              <FormRowTwoColumn>
                <FormLabelLeft>Business Type</FormLabelLeft>
                <FormInputRight>
                  <FieldValue>{organizationData.businessType || "Not set"}</FieldValue>
                </FormInputRight>
              </FormRowTwoColumn>

              {organizationData.businessType === "other" && (
                <>
                  <FormRowTwoColumn>
                    <FormLabelLeft>Number of team members</FormLabelLeft>
                    <FormInputRight>
                      <FieldValue>{organizationData.teamMembers || "Not set"}</FieldValue>
                    </FormInputRight>
                  </FormRowTwoColumn>

                  <FormRowTwoColumn>
                    <FormLabelLeft>Number of cards you have to list in total</FormLabelLeft>
                    <FormInputRight>
                      <FieldValue>{organizationData.totalCards || "Not set"}</FieldValue>
                    </FormInputRight>
                  </FormRowTwoColumn>
                </>
              )}
            </>
          )}
          </>
          )}
        </EditableSection>

        {/* Select Plan Section */}
        <EditableSection>
          <SectionHeader
            style={{ cursor: "pointer" }}
            onClick={() => setIsSelectPlanExpanded((v) => !v)}
          >
            <SectionHeaderTitle>Select Plan</SectionHeaderTitle>
            <ExpandToggleButton
              type="button"
              $expanded={isSelectPlanExpanded}
              aria-label={isSelectPlanExpanded ? "Collapse select plan" : "Expand select plan"}
              onClick={(e) => {
                e.stopPropagation();
                setIsSelectPlanExpanded((v) => !v);
              }}
            >
              <ChevronDown size={16} />
            </ExpandToggleButton>
          </SectionHeader>
          {isSelectPlanExpanded && (
          <PlansGrid>
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                $selected={selectedPlan === plan.id}
                onClick={() => handlePlanSelect(plan.id)}
              >
                {plan.badge && <PlanBadge>{plan.badge}</PlanBadge>}
                <PlanName>{plan.name}</PlanName>
                <PlanPrice>
                  {plan.price}
                  <span style={{ fontSize: "0.875rem", fontWeight: 400, color: "#4b5563" }}>
                    /{plan.period}
                  </span>
                </PlanPrice>
                <PlanFeatures>{plan.scans}</PlanFeatures>
                <PlanButton $selected={selectedPlan === plan.id}>
                  {plan.buttonText}
                </PlanButton>
              </PlanCard>
            ))}
          </PlansGrid>
          )}
        </EditableSection>

        <SettingsSection>
          <CollapsibleTitleRow
            style={{ marginBottom: "0.5rem" }}
            onClick={() => setIsSecurityExpanded((v) => !v)}
          >
            <SectionTitle style={{ marginBottom: 0 }}>Security</SectionTitle>
            <ExpandToggleButton
              type="button"
              $expanded={isSecurityExpanded}
              aria-label={isSecurityExpanded ? "Collapse security" : "Expand security"}
              onClick={(e) => {
                e.stopPropagation();
                setIsSecurityExpanded((v) => !v);
              }}
            >
              <ChevronDown size={16} />
            </ExpandToggleButton>
          </CollapsibleTitleRow>

          {isSecurityExpanded && (
            <>
              <SectionDescription>
                Manage your account security settings and password.
              </SectionDescription>
              <ChangePasswordButton onClick={handleOpenChangePasswordModal}>
                <Lock />
                Change Password
              </ChangePasswordButton>

              <DangerSection>
                <DangerTitle>Danger Zone</DangerTitle>
                <DangerDescription>
                  Once you delete your account, there is no going back. Please be certain.
                </DangerDescription>
                <DeleteAccountButton onClick={handleOpenDeleteModal}>
                  <Trash2 />
                  Delete Account
                </DeleteAccountButton>
              </DangerSection>
            </>
          )}
        </SettingsSection>
      </SettingsContent>

      {/* Profile Completion Modal */}
      <ModalOverlay $isOpen={isModalOpen} onClick={handleOverlayClick}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>
              <AlertCircle />
              Profile Completion is Mandatory
            </ModalTitle>
            <CloseButton onClick={handleCloseModal}>
              <X />
            </CloseButton>
          </ModalHeader>
          <ModalContent>
            <ImportanceSection>
              <ImportanceContent>
                <ImportanceTitle>Why Complete Your Profile?</ImportanceTitle>
                <ImportanceText>
                  Completing your profile is mandatory to ensure the best experience
                  and maintain the security and integrity of our platform. Here's why
                  it's important:
                </ImportanceText>
              </ImportanceContent>
            </ImportanceSection>

            <ReasonList>
              <ReasonItem>
                <ReasonIcon />
                <ReasonContent>
                  <ReasonTitle>Account Security</ReasonTitle>
                  <ReasonDescription>
                    A complete profile helps us verify your identity and protect your
                    account from unauthorized access. This ensures your data and
                    listings remain secure.
                  </ReasonDescription>
                </ReasonContent>
              </ReasonItem>

              <ReasonItem>
                <ReasonIcon />
                <ReasonContent>
                  <ReasonTitle>Personalized Experience</ReasonTitle>
                  <ReasonDescription>
                    With complete profile information, we can provide you with
                    personalized recommendations, relevant updates, and tailored
                    features that match your needs.
                  </ReasonDescription>
                </ReasonContent>
              </ReasonItem>

              <ReasonItem>
                <ReasonIcon />
                <ReasonContent>
                  <ReasonTitle>Communication & Support</ReasonTitle>
                  <ReasonDescription>
                    Accurate contact information ensures we can reach you with
                    important updates, support responses, and account notifications
                    when needed.
                  </ReasonDescription>
                </ReasonContent>
              </ReasonItem>

              <ReasonItem>
                <ReasonIcon />
                <ReasonContent>
                  <ReasonTitle>Platform Compliance</ReasonTitle>
                  <ReasonDescription>
                    Complete profiles help us maintain compliance with platform
                    policies and ensure all users meet our service requirements,
                    creating a trustworthy marketplace environment.
                  </ReasonDescription>
                </ReasonContent>
              </ReasonItem>

              <ReasonItem>
                <ReasonIcon />
                <ReasonContent>
                  <ReasonTitle>Feature Access</ReasonTitle>
                  <ReasonDescription>
                    Some advanced features and capabilities are only available to
                    users with completed profiles, ensuring optimal functionality
                    and service quality.
                  </ReasonDescription>
                </ReasonContent>
              </ReasonItem>
            </ReasonList>
          </ModalContent>
          <ModalFooter>
            <CloseModalButton onClick={handleCloseModal}>
              I Understand
            </CloseModalButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>

      {/* Delete Account Modal */}
      <ModalOverlay $isOpen={isDeleteModalOpen} onClick={handleDeleteOverlayClick}>
        <ModalContainer>
          <DeleteModalHeader>
            <DeleteModalTitle>
              <Trash2 />
              Delete Account
            </DeleteModalTitle>
            <CloseButton onClick={handleCloseDeleteModal}>
              <X />
            </CloseButton>
          </DeleteModalHeader>
          <DeleteModalContent>
            <WarningText>
              Are you sure you want to delete your account? This action cannot be undone.
              All your data, listings, and settings will be permanently deleted.
            </WarningText>
            <WarningText style={{ fontWeight: 600, color: "#dc2626", marginTop: "1rem" }}>
              Do you want to proceed with deleting your account?
            </WarningText>
          </DeleteModalContent>
          <DeleteModalFooter>
            <CancelButton onClick={handleCloseDeleteModal} disabled={isDeleting}>
              Cancel
            </CancelButton>
            <ConfirmDeleteButton
              onClick={handleDeleteAccount}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete Account"}
            </ConfirmDeleteButton>
          </DeleteModalFooter>
        </ModalContainer>
      </ModalOverlay>

      {/* Change Password Modal */}
      <ModalOverlay $isOpen={isChangePasswordModalOpen} onClick={handleChangePasswordOverlayClick}>
        <ModalContainer>
          <ChangePasswordModalHeader>
            <ChangePasswordModalTitle>
              <Lock />
              Change Password
            </ChangePasswordModalTitle>
            <CloseButton onClick={handleCloseChangePasswordModal}>
              <X />
            </CloseButton>
          </ChangePasswordModalHeader>
          <ChangePasswordModalContent>
            <FormGroup>
              <FormLabel htmlFor="current-password">Current Password</FormLabel>
              <PasswordInputWrapper>
                <PasswordInput
                  type={showCurrentPassword ? "text" : "password"}
                  id="current-password"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    setChangePasswordErrors({ ...changePasswordErrors, currentPassword: "" });
                  }}
                  placeholder="Enter your current password"
                  disabled={isChangingPassword}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                  disabled={isChangingPassword}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </PasswordInputWrapper>
              {changePasswordErrors.currentPassword && (
                <ErrorMessage>{changePasswordErrors.currentPassword}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="new-password">New Password</FormLabel>
              <PasswordInputWrapper>
                <PasswordInput
                  type={showNewPassword ? "text" : "password"}
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setChangePasswordErrors({ ...changePasswordErrors, newPassword: "" });
                  }}
                  placeholder="Enter your new password"
                  disabled={isChangingPassword}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                  disabled={isChangingPassword}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </PasswordInputWrapper>
              {changePasswordErrors.newPassword && (
                <ErrorMessage>{changePasswordErrors.newPassword}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="confirm-password">Confirm New Password</FormLabel>
              <PasswordInputWrapper>
                <PasswordInput
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setChangePasswordErrors({ ...changePasswordErrors, confirmPassword: "" });
                  }}
                  placeholder="Confirm your new password"
                  disabled={isChangingPassword}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isChangingPassword) {
                      handleChangePassword();
                    }
                  }}
                />
                <PasswordToggle
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  disabled={isChangingPassword}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </PasswordToggle>
              </PasswordInputWrapper>
              {changePasswordErrors.confirmPassword && (
                <ErrorMessage>{changePasswordErrors.confirmPassword}</ErrorMessage>
              )}
            </FormGroup>
          </ChangePasswordModalContent>
          <ChangePasswordModalFooter>
            <CancelButton onClick={handleCloseChangePasswordModal} disabled={isChangingPassword}>
              Cancel
            </CancelButton>
            <SavePasswordButton
              onClick={handleChangePassword}
              disabled={isChangingPassword}
            >
              {isChangingPassword ? "Changing..." : "Change Password"}
            </SavePasswordButton>
          </ChangePasswordModalFooter>
        </ModalContainer>
      </ModalOverlay>

      {/* Change Email Modal */}
      <ModalOverlay $isOpen={isChangeEmailModalOpen} onClick={handleChangeEmailOverlayClick}>
        <ModalContainer>
          <ChangeEmailModalHeader>
            <ChangeEmailModalTitle>
              <Mail />
              Change Email
            </ChangeEmailModalTitle>
            <CloseButton onClick={handleCloseChangeEmailModal}>
              <X />
            </CloseButton>
          </ChangeEmailModalHeader>
          <ChangeEmailModalContent>
            {verificationData ? (
              <>
                <VerificationMessage>
                  <VerificationText>
                    {verificationData.message || "Verification email sent to your new email address. Please verify to complete the email change."}
                  </VerificationText>
                  <VerificationLinkButton
                    onClick={handleVerifyEmail}
                    disabled={isVerifying}
                  >
                    <ExternalLink />
                    {isVerifying ? "Verifying..." : "Click here to verify"}
                  </VerificationLinkButton>
                </VerificationMessage>
                {changeEmailError && (
                  <ErrorMessage style={{ marginTop: "1rem" }}>{changeEmailError}</ErrorMessage>
                )}
              </>
            ) : (
              <FormGroup>
                <FormLabel htmlFor="new-email">New Email Address</FormLabel>
                <TextInput
                  type="email"
                  id="new-email"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setChangeEmailError("");
                  }}
                  placeholder="Enter your new email address"
                  disabled={isChangingEmail}
                />
                {changeEmailError && (
                  <ErrorMessage>{changeEmailError}</ErrorMessage>
                )}
              </FormGroup>
            )}
          </ChangeEmailModalContent>
          <ChangeEmailModalFooter>
            {verificationData ? (
              <CancelButton onClick={handleCloseChangeEmailModal} disabled={isVerifying}>
                Close
              </CancelButton>
            ) : (
              <>
                <CancelButton onClick={handleCloseChangeEmailModal} disabled={isChangingEmail}>
                  Cancel
                </CancelButton>
                <SaveEmailButton
                  onClick={handleChangeEmail}
                  disabled={isChangingEmail || !newEmail.trim()}
                >
                  {isChangingEmail ? "Updating..." : "Update Email"}
                </SaveEmailButton>
              </>
            )}
          </ChangeEmailModalFooter>
        </ModalContainer>
      </ModalOverlay>

      {/* Change Address Modal */}
      <ModalOverlay $isOpen={isAddressModalOpen} onClick={handleAddressOverlayClick}>
        <ModalContainer>
          <ChangeAddressModalHeader>
            <ChangeAddressModalTitle>
              <Edit2 />
              Edit Address
            </ChangeAddressModalTitle>
            <CloseButton onClick={handleAddressCancel}>
              <X />
            </CloseButton>
          </ChangeAddressModalHeader>
          <ChangeAddressModalContent>
            <ModalFormGroup>
              <FormLabel htmlFor="modal-country">Country</FormLabel>
              <SelectInput
                id="modal-country"
                value={tempAddressData.country}
                onChange={(e) => setTempAddressData({ ...tempAddressData, country: e.target.value })}
                style={{ width: "100%" }}
              >
                <option value="United States">United States</option>
                <option value="Canada">Canada</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Australia">Australia</option>
                <option value="Germany">Germany</option>
                <option value="France">France</option>
                <option value="Other">Other</option>
              </SelectInput>
            </ModalFormGroup>

            <ModalFormGroup>
              <FormLabel htmlFor="modal-addressLine1">Address</FormLabel>
              <TextInput
                id="modal-addressLine1"
                type="text"
                value={tempAddressData.addressLine1}
                onChange={(e) => setTempAddressData({ ...tempAddressData, addressLine1: e.target.value })}
                placeholder="Address"
                style={{ width: "100%" }}
              />
            </ModalFormGroup>

            <ModalFormGroup>
              <FormLabel htmlFor="modal-city">City</FormLabel>
              <TextInput
                id="modal-city"
                type="text"
                value={tempAddressData.city}
                onChange={(e) => setTempAddressData({ ...tempAddressData, city: e.target.value })}
                placeholder="City"
                style={{ width: "100%" }}
              />
            </ModalFormGroup>

            <ModalFormGroup>
              <FormLabel htmlFor="modal-state">State</FormLabel>
              <TextInput
                id="modal-state"
                type="text"
                value={tempAddressData.state}
                onChange={(e) => setTempAddressData({ ...tempAddressData, state: e.target.value })}
                placeholder="State"
                style={{ width: "100%" }}
              />
            </ModalFormGroup>

            <ModalFormGroup>
              <FormLabel htmlFor="modal-zipCode">Postal code</FormLabel>
              <TextInput
                id="modal-zipCode"
                type="text"
                value={tempAddressData.zipCode}
                onChange={(e) => setTempAddressData({ ...tempAddressData, zipCode: e.target.value })}
                placeholder="Postal code"
                style={{ width: "100%" }}
              />
            </ModalFormGroup>
          </ChangeAddressModalContent>
          <ChangeAddressModalFooter>
            <CancelButton onClick={handleAddressCancel} disabled={isAddressSaving}>
              Cancel
            </CancelButton>
            <SaveEmailButton
              onClick={handleAddressSave}
              disabled={isAddressSaving}
            >
              <Save />
              {isAddressSaving ? "Saving..." : "Save"}
            </SaveEmailButton>
          </ChangeAddressModalFooter>
        </ModalContainer>
      </ModalOverlay>

      {/* Change Organization Details Modal */}
      <ModalOverlay $isOpen={isOrganizationModalOpen} onClick={handleOrganizationOverlayClick}>
        <ModalContainer>
          <ChangeAddressModalHeader>
            <ChangeAddressModalTitle>
              <Edit2 />
              Organization Details
            </ChangeAddressModalTitle>
            <CloseButton onClick={handleOrganizationCancel}>
              <X />
            </CloseButton>
          </ChangeAddressModalHeader>
          <ChangeAddressModalContent>
            <ModalFormGroup>
              <FormLabel htmlFor="modal-businessName">Business Name</FormLabel>
              <TextInput
                id="modal-businessName"
                type="text"
                value={tempOrganizationData.businessName}
                onChange={(e) => setTempOrganizationData({ ...tempOrganizationData, businessName: e.target.value })}
                placeholder="Business Name"
                style={{ width: "100%" }}
              />
            </ModalFormGroup>

            <ModalFormGroup>
              <FormLabel>Business Type</FormLabel>
              <SelectionButtonGroup>
                {["private", "other"].map((option) => (
                  <SelectionButton
                    key={option}
                    type="button"
                    $selected={tempOrganizationData.businessType === option}
                    onClick={() => {
                      setTempOrganizationData({ ...tempOrganizationData, businessType: option });
                    }}
                  >
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </SelectionButton>
                ))}
              </SelectionButtonGroup>
            </ModalFormGroup>

            {tempOrganizationData.businessType === "other" && (
              <>
                <ModalFormGroup>
                  <FormLabel>Number of team members</FormLabel>
                  <SelectionButtonGroup>
                    {["1", "1-5", "5-10", "10-20", "20+"].map((option) => (
                      <SelectionButton
                        key={option}
                        type="button"
                        $selected={tempOrganizationData.teamMembers === option}
                        onClick={() => {
                          setTempOrganizationData({ ...tempOrganizationData, teamMembers: option });
                        }}
                      >
                        {option}
                      </SelectionButton>
                    ))}
                  </SelectionButtonGroup>
                </ModalFormGroup>

                <ModalFormGroup>
                  <FormLabel>Number of cards you have to list in total</FormLabel>
                  <SelectionButtonGroup>
                    {["0-500", "500-1,000", "1,000-10,000", "10,000-25,000", "25,000-50,000", "50,000+"].map((option) => (
                      <SelectionButton
                        key={option}
                        type="button"
                        $selected={tempOrganizationData.totalCards === option}
                        onClick={() => {
                          setTempOrganizationData({ ...tempOrganizationData, totalCards: option });
                        }}
                      >
                        {option}
                      </SelectionButton>
                    ))}
                  </SelectionButtonGroup>
                </ModalFormGroup>
              </>
            )}
          </ChangeAddressModalContent>
          <ChangeAddressModalFooter>
            <CancelButton onClick={handleOrganizationCancel} disabled={isOrganizationSaving}>
              Cancel
            </CancelButton>
            <SaveEmailButton
              onClick={handleOrganizationSave}
              disabled={isOrganizationSaving}
            >
              <Save />
              {isOrganizationSaving ? "Saving..." : "Save"}
            </SaveEmailButton>
          </ChangeAddressModalFooter>
        </ModalContainer>
      </ModalOverlay>




    </ProfileSettingsContainer>
    </ProfileSettingsPage>
  );
}

export default ProfileSettings;

